import { useQuery } from '@tanstack/react-query';
import { env } from '../utils/env';
import * as Sentry from '@sentry/react';
import { SimpleCategoryHeader, SimpleCategory, SpendingType } from '../types/budget';

interface UseCategoriesOptions {
    isVisible?: boolean;
}

// Define the expected structure from /api/budget/plan for headers and categories
interface BudgetPlanCategory {
    id: number;
    name: string;
    icon?: string;
    color?: string;
    // other fields from BudgetCategory in useBudgetCategories if needed for transformation
}

interface BudgetPlanHeader {
    id: number;
    name: string;
    categories: BudgetPlanCategory[];
    spending_type: SpendingType;
    // other fields from BudgetHeader in useBudgetCategories if needed for transformation
}

interface BudgetPlanResponse {
    headers: BudgetPlanHeader[];
    // other fields from BudgetPlanResponse in useBudgetCategories if needed for transformation
}

// Maps to backend/routers/budget.py
export function useCategories(accessToken: string, options: UseCategoriesOptions = {}) {
    const { isVisible = true } = options;

    const result = useQuery<SimpleCategoryHeader[], Error>({ // Explicitly type Error for TanStack Query v5 compatibility
        queryKey: ['categories', 'budgetPlanForCategories'], // Modified queryKey to reflect new source
        queryFn: async (): Promise<SimpleCategoryHeader[]> => {
            try {
                const response = await fetch(`${env.API_URL}/api/budget/plan`, { // Fetch full budget plan
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch budget plan for categories: ${response.status}`);
                }

                const data: BudgetPlanResponse = await response.json();

                if (data && data.headers && Array.isArray(data.headers)) {
                    return data.headers.map((header: BudgetPlanHeader) => ({
                        id: String(header.id), // Convert number to string
                        name: header.name,
                        spending_type: header.spending_type,
                        categories: Array.isArray(header.categories)
                            ? header.categories.map((cat: BudgetPlanCategory) => ({
                                id: String(cat.id), // Convert number to string
                                name: cat.name,
                                header_id: String(header.id),
                                icon: cat.icon,
                                color: cat.color,
                            }))
                            : [],
                    }));
                }
                return []; // Return empty array if headers are not as expected
            } catch (error) {
                console.error('Error fetching categories from budget plan:', error);
                Sentry.captureException(error, {
                    tags: {
                        component: 'useCategories',
                        action: 'fetchCategoriesFromPlan' // Updated action
                    },
                    extra: {
                        accessToken: accessToken ? 'present' : 'missing'
                    }
                });
                throw error; // Re-throw to be handled by useQuery
            }
        },
        enabled: isVisible && !!accessToken,
    });

    return {
        categories: result.data || [],
        isLoading: result.isLoading,
        error: result.error ? (result.error instanceof Error ? result.error.message : String(result.error)) : null,
        refetch: result.refetch
    };
} 